import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FaStar, FaMapMarkerAlt, FaCheckCircle } from "react-icons/fa";
import http from "../lib/http";
import "./DealerListPage.css";
import SEO from "../components/SEO/SEO";

/* -------------------- Helpers -------------------- */

const renderStars = (rating = 0) =>
  Array.from({ length: 5 }, (_, i) => (
    <FaStar
      key={i}
      color={i < Math.round(rating) ? "#f5b301" : "#e4e5e9"}
      size={14}
    />
  ));

const formatDistance = (meters) => {
  if (!meters) return null;

  const km = meters / 1000;

  if (km < 1) {
    return `${meters.toFixed(0)} m away`;
  }

  return `${km.toFixed(1)} km away`;
};

export default function DealerListPage() {
  const { search } = useLocation();
  const navigate = useNavigate();

  const query = new URLSearchParams(search);

  const lat = parseFloat(query.get("lat")) || null;
  const lng = parseFloat(query.get("lng")) || null;
  const category = query.get("category") || "";

  const [dealers, setDealers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  /* -------------------- GPS AUTO DETECTION -------------------- */

  useEffect(() => {
    if (lat && lng) return; // already provided

    if (!navigator.geolocation) {
      console.warn("Geolocation not supported");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const newLat = pos.coords.latitude;
        const newLng = pos.coords.longitude;

        const params = new URLSearchParams(search);
        params.set("lat", newLat);
        params.set("lng", newLng);

        navigate(`?${params.toString()}`, { replace: true });
      },
      (err) => {
        console.warn("Location permission denied", err);
      },
      {
        enableHighAccuracy: true, // 🔥 small improvement
        timeout: 8000,
        maximumAge: 60000 // 🔥 Chrome speed improvement
      }
    );
  }, [lat, lng, search, navigate]);

  /* -------------------- FETCH DEALERS -------------------- */

  useEffect(() => {
    const fetchDealers = async () => {
      try {
        setLoading(true);

        const params = {
          category,
          page,
          limit: 6,
          isActive: true,
        };

        if (lat && lng) {
          params.lat = lat;
          params.lng = lng;
        }

        const res = await http.get("/api/dealers/search", { params });

        setDealers(res.data.dealers || []);
        setTotalPages(res.data.totalPages || 1);
      } catch (err) {
        console.error("Dealer fetch error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchDealers();
  }, [lat, lng, category, page]);

  /* -------------------- UI STATES -------------------- */

  if (loading) {
    return <p className="text-center mt-5">Finding trusted dealers near you…</p>;
  }

  if (!dealers.length) {
  return (
    <div className="text-center py-5">
      <h4>No Dealers Found</h4>

      <p className="text-muted">
        Try searching another category or nearby location.
      </p>
    </div>
  );
}

  /* -------------------- UI -------------------- */

  return (
    <>
    <SEO
      title="Verified Construction Material Dealers | BuildNear"
    description="Browse verified construction material dealers, suppliers and hardware stores near your location."
    keywords="construction dealers, cement suppliers, tile suppliers, hardware stores, steel dealers"
    url="https://buildnear.in/dealers"
/>

    <div className="dealer-list-wrapper">
      <div className="container my-4">

        {/* HEADER */}
        <div className="mb-4">
          <div className="dealer-header mb-4">
            <h2 className="dealer-page-title">
              {category
                ? `${category} Dealers Near You`
                : "Trusted Dealers Near You"}
            </h2>

            <p className="dealer-page-subtitle">
              Verified suppliers • Genuine reviews • Fast response
            </p>
          </div>
        </div>

        {/* DEALERS */}
        <div className="row g-4">
          {dealers.map((d) => {
            const approvedReviews =
              d.reviews?.filter((r) => r.approved) || [];

            const reviewCount = approvedReviews.length;

            const rating =
              reviewCount > 0
                ? (
                    approvedReviews.reduce(
                      (sum, r) => sum + Number(r.rating || 0),
                      0
                    ) / reviewCount
                  )
                : 0;

            return (
              <div key={d._id} className="col-12 col-md-6 col-lg-4">
                <div className="dealer-card h-100">

                  {/* IMAGE */}
                  <div className="dealer-img">

                    <img
                      src={d.image || "/images/dealer-placeholder.jpg"}
                      alt={d.name}
                      loading="lazy"
                    />

                    {typeof d.distance === "number" && (
                      <div className="distance-badge">
                        📍 {formatDistance(d.distance)}
                      </div>
                    )}

                    {rating >= 4.5 && reviewCount >= 5 && (
                      <div className="featured-badge">
                        ⭐ Featured
                      </div>
                    )}

                  </div>

                  {/* CONTENT */}
                  <div className="dealer-body">

                    {/* NAME */}
                    <h5 className="dealer-name">
                      {d.name}

                      <FaCheckCircle
                        className="ms-2 text-success"
                        title="Verified Dealer"
                        size={14}
                      />
                    </h5>

                    {/* LOCATION */}
                    <p className="dealer-location">
                      <FaMapMarkerAlt /> {d.locationName || d.address}
                    </p>

                    {/* ETA */}
                    <div className="dealer-highlights">

                      {d.inventoryAvailable && (
                        <span className="highlight-pill stock">
                          ✓ In Stock
                        </span>
                      )}

                      {d.estimatedDeliveryTime && (
                        <span className="highlight-pill eta">
                          🚚 {d.estimatedDeliveryTime}
                        </span>
                      )}

                    </div>

                    {/* RATING */}
                    {reviewCount >= 10 && (
                          <div className="trust-indicator">
                            🏆 Trusted by local customers
                          </div>
                        )}
                    <div className="dealer-rating">
                        
                    {reviewCount > 0 ? (
                      <>
                        {renderStars(rating)}

                        <span className="rating-number ms-2">
                          {rating.toFixed(1)}
                        </span>

                        <span className="ms-2 small text-muted">
                          ({reviewCount} verified reviews)
                        </span>
                      </>
                    ) : (
                      <span className="text-muted small">
                        No verified reviews yet
                      </span>
                    )}
                  </div>

                    {/* ACTION */}
                    <button
                        className="dealer-view-btn"
                        onClick={() => navigate(`/dealer/${d._id}`)}
                      >
                        View Dealer Details →
                      </button>

                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* PAGINATION */}
        {totalPages > 1 && (
          <nav className="d-flex justify-content-center mt-5">
            <ul className="pagination">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                <li key={p} className={`page-item ${p === page ? "active" : ""}`}>
                  <button className="page-link" onClick={() => setPage(p)}>
                    {p}
                  </button>
                </li>
              ))}
            </ul>
          </nav>
        )}
      </div>

    </div>
        </>
  );
}